/**
 * Локальные черновики заявок (п. 9 ТЗ).
 *
 * Между шагами формы связь может пропасть, и введённое монтажником не должно
 * исчезнуть. Поэтому каждое изменение поля пишется в localStorage сразу, а
 * отправка на сервер идёт при переходе к следующему шагу. Если PATCH не прошёл,
 * черновик остаётся, и после восстановления связи повтор отправит те же данные.
 *
 * Черновик удаляется, когда сервер подтвердил сохранение, — чтобы устаревшая
 * локальная копия не перекрыла актуальные данные заявки.
 *
 * Важно: в localStorage попадают персональные данные. Черновик живёт до
 * подтверждения сервером и чистится при выходе из приложения (см. logout).
 */

const PREFIX = 'online-dogovor.draft.'
// Черновик старше суток — почти наверняка мусор от брошенной заявки.
const MAX_AGE_MS = 24 * 60 * 60 * 1000

interface DraftEnvelope<T> {
  savedAt: number
  values: T
}

function key(applicationId: string): string {
  return `${PREFIX}${applicationId}`
}

export function saveDraft<T extends object>(applicationId: string, values: T): void {
  const envelope: DraftEnvelope<T> = { savedAt: Date.now(), values }
  try {
    localStorage.setItem(key(applicationId), JSON.stringify(envelope))
  } catch {
    // Квота исчерпана или приватный режим — черновик не критичен для работы,
    // данные всё равно уйдут на сервер на следующем шаге.
  }
}

export function loadDraft<T extends object>(applicationId: string): T | null {
  const raw = localStorage.getItem(key(applicationId))
  if (!raw) return null
  try {
    const envelope = JSON.parse(raw) as DraftEnvelope<T>
    if (!envelope?.values || Date.now() - envelope.savedAt > MAX_AGE_MS) {
      clearDraft(applicationId)
      return null
    }
    return envelope.values
  } catch {
    clearDraft(applicationId)
    return null
  }
}

export function clearDraft(applicationId: string): void {
  localStorage.removeItem(key(applicationId))
}

/** Удаляет все черновики — вызывается при выходе из учётной записи. */
export function clearAllDrafts(): void {
  const keys: string[] = []
  for (let index = 0; index < localStorage.length; index += 1) {
    const item = localStorage.key(index)
    if (item?.startsWith(PREFIX)) keys.push(item)
  }
  keys.forEach((item) => localStorage.removeItem(item))
}

export function hasDraft(applicationId: string): boolean {
  return localStorage.getItem(key(applicationId)) !== null
}
